import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import TopNav from "../../components/navigation/TopNav";
import { playSound } from "../../components/helpers/sound";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import TextInput from "../../components/forms/TextInput";
import StatusMessage from "../../components/ui/StatusMessage";

const narrowShellStyle = {
  width: "100%",
  maxWidth: "720px",
  margin: "0 auto",
};

const sectionTitleStyle = {
  margin: "0 0 0.75rem",
  fontSize: "1.05rem",
  fontWeight: 800,
};

export default function GodCreateVendorAndAdmin() {
  const { t } = useTranslation();

  const emptyForm = {
    vendor_name: "",
    vendor_tax_id: "",
    vendor_email: "",
    vendor_phone: "",
    vendor_notes: "",

    admin_userno: "",
    admin_fullname: "",
    admin_password: "",
    admin_email: "",
  };

  const [form, setForm] = useState(emptyForm);
  const [message, setMessage] = useState("");
  const [verdict, setVerdict] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [lastAction, setLastAction] = useState("");

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
  }, []);

  const getErrorMessage = (err, fallbackKey) => {
    const detail = err?.response?.data?.detail;

    if (typeof detail === "string") return detail;

    if (Array.isArray(detail)) {
      return detail
        .map((item) => {
          const location = Array.isArray(item.loc) ? item.loc.join(".") : "";
          return location ? `${location}: ${item.msg}` : item.msg;
        })
        .filter(Boolean)
        .join(" | ");
    }

    return t(fallbackKey);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setMessage("");
    setVerdict("");
    setLastAction("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.vendor_name.trim()) {
      setMessage(t("god_create_vendor.messages.vendor_name_required"));
      setVerdict("cancelled");
      playSound("error");
      return;
    }

    if (!form.admin_userno.trim()) {
      setMessage(t("god_create_vendor.messages.admin_userno_required"));
      setVerdict("cancelled");
      playSound("error");
      return;
    }

    if (!/^\d{4}$/.test(form.admin_password)) {
      setMessage(t("god_create_vendor.messages.admin_password_invalid"));
      setVerdict("cancelled");
      playSound("error");
      return;
    }

    try {
      setIsLoading(true);
      setMessage("");
      setVerdict("");
      setLastAction("create");

      await axios.post(`${process.env.REACT_APP_API_URL}/god/create-vendor`, form);

      setMessage(t("god_create_vendor.messages.success"));
      setVerdict("approved");
      playSound("success");

      setForm(emptyForm);
    } catch (err) {
      setMessage(getErrorMessage(err, "god_create_vendor.messages.failure"));
      setVerdict("cancelled");
      playSound("error");
    } finally {
      setIsLoading(false);
    }
  };

  const messageVariant =
    verdict === "approved"
      ? "success"
      : message
      ? "error"
      : "info";

  return (
    <StandardScreen
      topNav={
        <TopNav
          title={t("god_create_vendor.title")}
          mode="main"
          titleTo="/god"
        />
      }
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">
            <Card title={t("god_create_vendor.title")}>
              <form onSubmit={handleSubmit}>
                <Stack gap="4">
                  <div>
                    <h3 style={sectionTitleStyle}>
                      {t("god_create_vendor.sections.vendor_details")}
                    </h3>

                    <Stack gap="3">
                      <FormField label={t("god_create_vendor.fields.vendor_name.label")}>
                        <TextInput
                          name="vendor_name"
                          value={form.vendor_name}
                          onChange={handleChange}
                          placeholder={t("god_create_vendor.fields.vendor_name.placeholder")}
                          disabled={isLoading}
                        />
                      </FormField>

                      <FormField label={t("god_create_vendor.fields.vendor_tax_id.label")}>
                        <TextInput
                          name="vendor_tax_id"
                          value={form.vendor_tax_id}
                          onChange={handleChange}
                          placeholder={t("god_create_vendor.fields.vendor_tax_id.placeholder")}
                          disabled={isLoading}
                        />
                      </FormField>

                      <FormField label={t("god_create_vendor.fields.vendor_email.label")}>
                        <TextInput
                          name="vendor_email"
                          type="email"
                          value={form.vendor_email}
                          onChange={handleChange}
                          placeholder={t("god_create_vendor.fields.vendor_email.placeholder")}
                          disabled={isLoading}
                        />
                      </FormField>

                      <FormField label={t("god_create_vendor.fields.vendor_phone.label")}>
                        <TextInput
                          name="vendor_phone"
                          type="tel"
                          value={form.vendor_phone}
                          onChange={handleChange}
                          placeholder={t("god_create_vendor.fields.vendor_phone.placeholder")}
                          disabled={isLoading}
                        />
                      </FormField>

                      <FormField label={t("god_create_vendor.fields.vendor_notes.label")}>
                        <TextInput
                          name="vendor_notes"
                          value={form.vendor_notes}
                          onChange={handleChange}
                          placeholder={t("god_create_vendor.fields.vendor_notes.placeholder")}
                          disabled={isLoading}
                        />
                      </FormField>
                    </Stack>
                  </div>

                  <div>
                    <h3 style={sectionTitleStyle}>
                      {t("god_create_vendor.sections.vendor_admin")}
                    </h3>

                    <Stack gap="3">
                      <FormField label={t("god_create_vendor.fields.admin_userno.label")}>
                        <TextInput
                          name="admin_userno"
                          type="tel"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={form.admin_userno}
                          onChange={handleChange}
                          placeholder={t("god_create_vendor.fields.admin_userno.placeholder")}
                          disabled={isLoading}
                        />
                      </FormField>

                      <FormField label={t("god_create_vendor.fields.admin_fullname.label")}>
                        <TextInput
                          name="admin_fullname"
                          value={form.admin_fullname}
                          onChange={handleChange}
                          placeholder={t("god_create_vendor.fields.admin_fullname.placeholder")}
                          disabled={isLoading}
                        />
                      </FormField>

                      <FormField label={t("god_create_vendor.fields.admin_password.label")}>
                        <TextInput
                          name="admin_password"
                          type="password"
                          inputMode="numeric"
                          value={form.admin_password}
                          onChange={handleChange}
                          placeholder={t("god_create_vendor.fields.admin_password.placeholder")}
                          disabled={isLoading}
                        />
                      </FormField>

                      <FormField label={t("god_create_vendor.fields.admin_email.label")}>
                        <TextInput
                          name="admin_email"
                          type="email"
                          value={form.admin_email}
                          onChange={handleChange}
                          placeholder={t("god_create_vendor.fields.admin_email.placeholder")}
                          disabled={isLoading}
                        />
                      </FormField>
                    </Stack>
                  </div>

                  <Button
                    type="submit"
                    fullWidth
                    size="lg"
                    disabled={isLoading || (lastAction === "create" && verdict === "approved")}
                  >
                    {isLoading
                      ? t("god_create_vendor.status.creating")
                      : verdict === "approved"
                      ? t("god_create_vendor.status.success_short")
                      : verdict === "cancelled"
                      ? t("god_create_vendor.status.error_short")
                      : t("god_create_vendor.actions.submit")}
                  </Button>

                  <StatusMessage variant={messageVariant} message={message} />
                </Stack>
              </form>
            </Card>
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}